import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Plus, X, GitCompare, Check } from "lucide-react";
import { fetchPrinters } from "@/lib/api";
import StarRating from "@/components/StarRating";
import { TYPE_LABELS } from "@/lib/constants";
import { toast } from "sonner";

const MAX_COMPARE = 3;

export default function Compare() {
    const [params, setParams] = useSearchParams();
    const [all, setAll] = useState([]);
    const [selected, setSelected] = useState(() => {
        const ids = params.get("ids");
        return ids ? ids.split(",").filter(Boolean) : [];
    });

    useEffect(() => {
        fetchPrinters().then(setAll).catch(() => setAll([]));
    }, []);

    useEffect(() => {
        if (selected.length === 0) {
            setParams({}, { replace: true });
        } else {
            setParams({ ids: selected.join(",") }, { replace: true });
        }
    }, [selected, setParams]);

    const items = useMemo(
        () => selected.map(id => all.find(p => p.id === id)).filter(Boolean),
        [selected, all]
    );

    const addPrinter = (id) => {
        if (selected.includes(id)) return;
        if (selected.length >= MAX_COMPARE) {
            toast.error(`You can compare up to ${MAX_COMPARE} printers at a time.`);
            return;
        }
        setSelected([...selected, id]);
    };

    const removePrinter = (id) => {
        setSelected(selected.filter(x => x !== id));
    };

    const clear = () => setSelected([]);

    // Union of all spec keys, preserving encounter order
    const specKeys = useMemo(() => {
        const seen = new Set();
        const keys = [];
        items.forEach(p => {
            Object.keys(p.specs || {}).forEach(k => {
                if (!seen.has(k)) { seen.add(k); keys.push(k); }
            });
        });
        return keys;
    }, [items]);

    const availableForAdd = all.filter(p => !selected.includes(p.id));

    return (
        <div data-testid="compare-page" className="container-tight py-16 md:py-24">
            <div className="border-b border-zinc-800 pb-10 mb-10">
                <div className="eyebrow">Comparison Tool</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mt-3">
                    Compare specs, <span className="text-zinc-500">side by side.</span>
                </h1>
                <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
                    Add up to {MAX_COMPARE} printers to see their specs, ratings, and prices in one strict table.
                </p>
            </div>

            {/* Selected slots */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8" data-testid="compare-slots">
                {Array.from({ length: MAX_COMPARE }).map((_, idx) => {
                    const p = items[idx];
                    if (p) {
                        return (
                            <div key={p.id} className="card-flat relative overflow-hidden" data-testid={`compare-slot-${idx}`}>
                                <button
                                    onClick={() => removePrinter(p.id)}
                                    data-testid={`remove-slot-${idx}`}
                                    className="absolute top-3 right-3 z-10 h-8 w-8 inline-flex items-center justify-center bg-zinc-950/85 border border-zinc-800 hover:border-red-500 hover:text-red-400 text-zinc-400 rounded-sm transition-colors"
                                    aria-label="Remove"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                                <div className="aspect-[5/3] bg-zinc-900">
                                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="p-5">
                                    <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">{p.brand} · {TYPE_LABELS[p.type]}</div>
                                    <div className="font-display text-lg font-semibold text-white tracking-tight mt-2">{p.name}</div>
                                    <div className="mt-3 flex items-center justify-between">
                                        <StarRating value={p.rating} size="sm" />
                                        <div className="font-mono text-sm text-white">${p.price_usd.toFixed(0)}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div
                            key={idx}
                            data-testid={`empty-slot-${idx}`}
                            className="border-2 border-dashed border-zinc-800 rounded-sm p-6 flex flex-col items-center justify-center min-h-[220px] text-center"
                        >
                            <div className="h-10 w-10 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500">
                                <Plus className="h-5 w-5" />
                            </div>
                            <div className="mt-3 text-sm text-zinc-500">Slot {idx + 1}</div>
                            <div className="text-xs text-zinc-600 mt-1">Pick a printer below</div>
                        </div>
                    );
                })}
            </div>

            {/* Comparison table */}
            {items.length >= 2 ? (
                <div className="border border-zinc-800 rounded-sm overflow-x-auto" data-testid="compare-table">
                    <table className="w-full min-w-[720px] text-sm">
                        <thead className="bg-zinc-950 sticky top-0 z-10">
                            <tr>
                                <th className="text-left px-5 py-4 border-b border-zinc-800 text-xs tracking-[0.2em] uppercase text-zinc-500 w-56">Spec</th>
                                {items.map(p => (
                                    <th key={p.id} className="text-left px-5 py-4 border-b border-l border-zinc-800">
                                        <div className="font-display font-semibold text-white text-base tracking-tight">{p.name}</div>
                                        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{p.brand}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="text-zinc-200">
                            <tr>
                                <td className="px-5 py-4 border-b border-zinc-900 text-xs tracking-widest uppercase text-zinc-500">Rating</td>
                                {items.map(p => (
                                    <td key={p.id} className="px-5 py-4 border-b border-l border-zinc-900"><StarRating value={p.rating} size="sm" /></td>
                                ))}
                            </tr>
                            <tr>
                                <td className="px-5 py-4 border-b border-zinc-900 text-xs tracking-widest uppercase text-zinc-500">Price</td>
                                {items.map(p => (
                                    <td key={p.id} className="px-5 py-4 border-b border-l border-zinc-900 font-mono text-white">${p.price_usd.toFixed(2)}</td>
                                ))}
                            </tr>
                            <tr>
                                <td className="px-5 py-4 border-b border-zinc-900 text-xs tracking-widest uppercase text-zinc-500">Year</td>
                                {items.map(p => (
                                    <td key={p.id} className="px-5 py-4 border-b border-l border-zinc-900">{p.year}</td>
                                ))}
                            </tr>
                            {specKeys.map((key, i) => (
                                <tr key={key} className={i % 2 === 0 ? "bg-zinc-950/40" : ""}>
                                    <td className="px-5 py-4 border-b border-zinc-900 text-xs tracking-widest uppercase text-zinc-500">{key}</td>
                                    {items.map(p => (
                                        <td key={p.id} className="px-5 py-4 border-b border-l border-zinc-900">
                                            {p.specs?.[key] || <span className="text-zinc-600">—</span>}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            <tr>
                                <td className="px-5 py-4 text-xs tracking-widest uppercase text-zinc-500">Read Review</td>
                                {items.map(p => (
                                    <td key={p.id} className="px-5 py-4 border-l border-zinc-900">
                                        <Link to={`/reviews/${p.slug}`} className="text-blue-400 hover:text-blue-300 text-sm underline underline-offset-4">Full review →</Link>
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="card-flat p-10 text-center" data-testid="compare-hint">
                    <GitCompare className="h-8 w-8 text-zinc-600 mx-auto" />
                    <div className="mt-4 text-zinc-300">
                        {items.length === 0 ? "Select at least 2 printers to compare." : "Add one more printer to see the comparison table."}
                    </div>
                </div>
            )}

            {selected.length > 0 && (
                <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-zinc-500">{selected.length} of {MAX_COMPARE} selected</div>
                    <button onClick={clear} data-testid="clear-compare" className="text-sm text-zinc-400 hover:text-white transition-colors">Clear selection</button>
                </div>
            )}

            {/* Add printer picker */}
            <div className="mt-16 border-t border-zinc-800 pt-12">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="eyebrow">Available Printers</div>
                        <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white mt-2">Add to comparison</h2>
                    </div>
                    <div className="text-sm text-zinc-500">{availableForAdd.length} available</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableForAdd.map(p => (
                        <button
                            key={p.id}
                            onClick={() => addPrinter(p.id)}
                            data-testid={`add-${p.slug}`}
                            className="group card-flat p-4 flex items-center gap-4 text-left hover:border-blue-500/60 transition-colors duration-200"
                        >
                            <div className="h-16 w-16 flex-shrink-0 rounded-sm overflow-hidden bg-zinc-900">
                                <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{p.brand}</div>
                                <div className="font-display text-sm font-semibold text-white truncate group-hover:text-blue-400 transition-colors">{p.name}</div>
                                <div className="mt-1"><StarRating value={p.rating} size="sm" showValue={false} /></div>
                            </div>
                            <div className="h-8 w-8 rounded-sm border border-zinc-800 group-hover:border-blue-500 group-hover:text-blue-400 flex items-center justify-center text-zinc-400 transition-colors">
                                <Plus className="h-4 w-4" />
                            </div>
                        </button>
                    ))}
                    {availableForAdd.length === 0 && (
                        <div className="col-span-full text-center py-10 text-zinc-500 text-sm flex items-center justify-center gap-2">
                            <Check className="h-4 w-4" /> All printers added
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
